import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router';
import { LoginPage } from '@/features/auth/LoginPage';
import { useSession } from '@/features/auth/useSession';
import { Shell } from '@/features/shell/Shell';
import { MODES, type Mode } from '@/state/store';

/** Development harness for the globe engine (driven by e2e/globe.spec.ts). */
const GlobeHarness = lazy(() => import('@/features/loader/globe/GlobeHarness'));
const AdminPage = lazy(() => import('@/features/admin/AdminPage'));
const TwinHarness = lazy(() => import('@/features/twin/scene/TwinHarness'));
const MobilePage = lazy(() => import('@/features/mobile/MobilePage'));

export function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/*" element={<Protected />} />
    </Routes>
  );
}

/** The Worker already redirects unauthenticated navigations, so this mostly
 *  handles a session that expires while the app is open. */
function Protected() {
  const session = useSession();
  const location = useLocation();

  if (session.isPending) return <Splash />;
  if (session.isError) {
    if (session.error.status === 401) {
      const returnTo = location.pathname + location.search;
      window.location.assign('/login' + (returnTo !== '/' ? '?returnTo=' + encodeURIComponent(returnTo) : ''));
      return <Splash />;
    }
    return <Splash message="Cannot reach the server. Retrying." />;
  }

  return (
    <Routes>
      <Route path="/" element={<Navigate to="/overview" replace />} />
      {MODES.map((mode) => (
        <Route key={mode} path={`/${mode}/*`} element={<Shell mode={mode as Mode} user={session.data} />} />
      ))}
      <Route path="/twin/:assetId" element={<Shell mode="maintenance" user={session.data} />} />
      <Route
        path="/mobile"
        element={
          <Suspense fallback={<Splash message="Loading the tracker." />}>
            <MobilePage />
          </Suspense>
        }
      />
      <Route
        path="/admin/*"
        element={
          <Suspense fallback={<Splash message="Loading the admin console." />}>
            <AdminPage />
          </Suspense>
        }
      />
      {import.meta.env.DEV && (
        <Route
          path="/dev/twin"
          element={
            <Suspense fallback={<Splash message="Loading the twin harness." />}>
              <TwinHarness />
            </Suspense>
          }
        />
      )}
      {import.meta.env.DEV && (
        <Route
          path="/dev/globe"
          element={
            <Suspense fallback={<Splash message="Loading the globe harness." />}>
              <GlobeHarness />
            </Suspense>
          }
        />
      )}
      <Route path="*" element={<Navigate to="/overview" replace />} />
    </Routes>
  );
}

function Splash({ message }: { message?: string }) {
  return (
    <div className="hud-grid flex h-full items-center justify-center">
      <div className="flex flex-col items-center gap-3">
        <img src="/images/entarian-logo.svg" alt="Entarian" className="h-7 opacity-80" />
        <div className="text-xs tracking-[0.3em] text-ink-3">GEM DT</div>
        {message && <div className="text-sm text-ink-2">{message}</div>}
      </div>
    </div>
  );
}
